import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CopyCheckIcon, CopyLinkIcon } from "../Icons/index";
import { Typo } from "../Typo";

type CopyableLinkProps = {
  url: string;
  onCopy?: (source: "link" | "icon") => void;
};

export const CopyableLink = ({ url, onCopy }: CopyableLinkProps) => {
  const { t } = useTranslation();
  const translatedContent = {
    copyLinkTooltip: t(
      "overviewForCompleteIBUserPage_referralLink_copy_link_tooltip",
    ),
    linkCopiedTooltip: t(
      "overviewForCompleteIBUserPage_referralLink_link_copied_tooltip",
    ),
  };
  const [copiedFrom, setCopiedFrom] = useState<"link" | "icon" | undefined>();
  const [isLinkHovered, setIsLinkHovered] = useState(false);

  useEffect(() => {
    if (copiedFrom) {
      const timeout = setTimeout(() => {
        setCopiedFrom(undefined);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [copiedFrom]);

  const handleCopy = async (source: "link" | "icon") => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else if (
        typeof window !== "undefined" &&
        typeof window.prompt === "function"
      ) {
        const result = window.prompt(translatedContent.copyLinkTooltip, url);
        if (result === null) {
          return;
        }
      } else {
        return;
      }
      setCopiedFrom(source);
      onCopy?.(source);
    } catch {
      // Swallow errors; do not update UI to "copied" on failure.
    }
  };

  return (
    <div className="flex min-w-0 max-w-full items-center gap-2">
      <div
        className="relative min-w-0 flex-1"
        onMouseEnter={() => setIsLinkHovered(true)}
        onMouseLeave={() => setIsLinkHovered(false)}
      >
        {(isLinkHovered || copiedFrom === "link") && (
          <CopyTooltip
            text={
              copiedFrom === "link"
                ? translatedContent.linkCopiedTooltip
                : translatedContent.copyLinkTooltip
            }
          />
        )}
        <button
          type="button"
          className="block max-w-full cursor-pointer"
          onClick={() => handleCopy("link")}
          data-testid="referral-link-text"
        >
          <Typo
            element="span"
            type="labelMD"
            smType="labelSM"
            textColor="text-text-brand-default"
            className="break-all text-center underline lg:line-clamp-2 lg:break-all"
          >
            {url}
          </Typo>
        </button>
      </div>

      <button
        type="button"
        className="relative flex shrink-0 cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-surface-secondary"
        onClick={() => handleCopy("icon")}
        aria-label={translatedContent.copyLinkTooltip}
        data-testid="referral-copy-icon"
      >
        {copiedFrom ? (
          <CopyCheckIcon data-testid="check-icon" />
        ) : (
          <CopyLinkIcon data-testid="copy-icon" />
        )}
      </button>
    </div>
  );
};

const CopyTooltip = ({ text }: { text: string }) => (
  <div
    role="tooltip"
    className="absolute -top-9 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-surface-inverse-primary px-3 py-1.5 text-sm text-text-inverse-primary shadow-md"
  >
    {text}
    <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-surface-inverse-primary" />
  </div>
);
